import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "../../hooks";
import { DIALOG_ACTIONS_SX, DIALOG_PAPER_SX, DIALOG_TITLE_SX } from "./ui/dialogStyles";
import { PortalPackService } from "../../services/portal/PortalPackService";
import { extractApiError } from "../../utils/extractApiError";
const GENDERS = ["MALE", "FEMALE", "OTHER"];
export function AddPatientDialog({ open, addressId, onClose, onAdded, }) {
    const { t } = useTranslation("portal");
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [firstName, setFirstName] = React.useState("");
    const [lastName, setLastName] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [gender, setGender] = React.useState("");
    const mutation = useMutation({
        mutationFn: (payload) => PortalPackService.createPatient(payload, addressId),
        onSuccess: () => {
            onClose();
            onAdded();
        },
    });
    React.useEffect(() => {
        if (!open)
            return;
        mutation.reset();
        setFirstName("");
        setLastName("");
        setEmail("");
        setGender("");
    }, [open]);
    return (_jsxs(Dialog, { open: open, onClose: mutation.isPending ? undefined : onClose, fullWidth: true, maxWidth: "xs", fullScreen: fullScreen, slotProps: { paper: { sx: DIALOG_PAPER_SX } }, children: [_jsx(DialogTitle, { sx: DIALOG_TITLE_SX, children: t("addMemberTitle", "Add someone to this number") }), _jsx(DialogContent, { children: _jsxs(Stack, { spacing: 2, sx: { mt: 0.5 }, children: [_jsx(TextField, { fullWidth: true, size: "small", required: true, label: t("firstName", "First name"), value: firstName, onChange: (event) => setFirstName(event.target.value), disabled: mutation.isPending }), _jsx(TextField, { fullWidth: true, size: "small", label: t("lastName", "Last name"), value: lastName, onChange: (event) => setLastName(event.target.value), disabled: mutation.isPending }), _jsx(TextField, { fullWidth: true, size: "small", type: "email", label: t("email", "Email (optional)"), value: email, onChange: (event) => setEmail(event.target.value), disabled: mutation.isPending }), _jsx(TextField, { select: true, fullWidth: true, size: "small", label: t("gender", "Gender"), value: gender, onChange: (event) => setGender(event.target.value), disabled: mutation.isPending, children: GENDERS.map((value) => (_jsx(MenuItem, { value: value, children: value }, value))) }), mutation.isError ? (_jsx(Alert, { severity: "error", sx: { borderRadius: 2 }, children: extractApiError(mutation.error, t("addMemberFailed", "We couldn't save that record.")) })) : null] }) }), _jsxs(DialogActions, { sx: DIALOG_ACTIONS_SX, children: [_jsx(Button, { onClick: onClose, disabled: mutation.isPending, sx: { borderRadius: 2 }, children: t("dismiss", "Cancel") }), _jsx(Button, { variant: "contained", sx: { borderRadius: 2 }, disabled: mutation.isPending || firstName.trim() === "", onClick: () => mutation.mutate({
                            firstName: firstName.trim(),
                            lastName: lastName.trim() || undefined,
                            email: email.trim() || undefined,
                            gender: gender || undefined,
                        }), children: mutation.isPending ? (_jsx(CircularProgress, { size: 18, color: "inherit" })) : (t("save", "Save")) })] })] }));
}
